import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { amenitiesApi, type AmenitySessionOut } from '@/api/amenities';
import { VacuumCountdown } from '@/components/amenities/VacuumCountdown';
import { friendlyMessage } from '@/lib/errors';

const POLL_MS = 5_000;

/**
 * VacuumActive — tela imersiva (full-bleed navy) com o cronômetro VISUAL do
 * aspirador (fluxo logado). O corte real é garantido pelo hardware
 * (`toggle_after` do Shelly). Miolo visual compartilhado com a página
 * pública do Pix avulso — `VacuumCountdown` (extração 2026-07-08).
 */
export default function VacuumActive() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<AmenitySessionOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Carrega + poll do estado da sessão.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const apply = (s: AmenitySessionOut) => {
      if (!cancelled) setSession(s);
    };

    amenitiesApi
      .getSession(sessionId)
      .then(apply)
      .catch((err) => {
        if (!cancelled) setError(friendlyMessage(err, 'Falha ao carregar sessão'));
      });

    const id = window.setInterval(() => {
      amenitiesApi
        .getSession(sessionId)
        .then(apply)
        .catch(() => {
          /* silencioso — mantém o último estado */
        });
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sessionId]);

  // Pílula "Aspirando" some quando o bloco acaba — aí o próprio
  // VacuumCountdown já mostra o estado final e o botão de saída.
  const isTerminal =
    session != null && (session.state === 'completed' || session.state === 'failed');

  return (
    <div className="relative flex h-[100dvh] flex-col items-center justify-center overflow-hidden bg-bow-royal px-6 text-bow-cream">
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full"
        style={{ background: 'var(--grad-arc-cool)', opacity: 0.2, filter: 'blur(60px)' }}
      />

      {/* Header — live pill + X, espelhando ActiveCharge. ABSOLUTE de
          propósito: o miolo desta tela é centralizado por `justify-center`
          num nível só, então um header no fluxo empurraria o anel do
          cronômetro pra baixo. Sair NÃO desliga o aspirador — quem corta é o
          `toggle_after` do hardware. */}
      <div
        className="absolute inset-x-6 z-10 flex items-center justify-between"
        style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
      >
        {session && !isTerminal ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-bow-cream/15 bg-bow-cream/10 px-3.5 py-2 text-xs font-semibold backdrop-blur-md">
            <span
              className="inline-block h-[7px] w-[7px] animate-pulse rounded-full bg-bow-green"
              style={{ boxShadow: '0 0 8px #00C072' }}
            />
            Aspirando
          </span>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => navigate('/', { replace: true })}
          aria-label="Fechar"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-bow-cream/15 bg-bow-cream/10 backdrop-blur-md transition hover:bg-bow-cream/15"
        >
          <CloseIcon />
        </button>
      </div>

      {error && !session ? (
        <div className="relative text-center">
          <p className="text-sm text-bow-cream/80">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-4 inline-flex h-12 items-center justify-center rounded-full bg-bow-cream/15 px-8 text-sm font-semibold text-bow-cream backdrop-blur transition hover:bg-bow-cream/25"
          >
            Voltar ao início
          </button>
        </div>
      ) : !session ? (
        <div className="relative h-40 w-40 animate-pulse rounded-full bg-white/10" />
      ) : (
        <VacuumCountdown session={session} onExit={() => navigate('/')} />
      )}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
